import type { ReactNode } from 'react';
import { SidebarProvider } from '@/context/SidebarContext';
import { useSidebar } from '@/context/sidebar';
import { useAuth } from '@/store/useAppStore';
import { Role } from '@/types';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { MobileBottomBar } from './MobileBottomBar';
import { MobileTopActions } from './MobileTopActions';
import type { SidebarLink } from './Sidebar';
import { cn } from '@/utils/cn';
import { GamificationAnimations } from '@/features/gamification';

interface AppLayoutProps {
  children: ReactNode;
  sidebarLinks: SidebarLink[];
}

/**
 * Layout principal modernisé
 * Structure: Header Fixe + Sidebar Flottante + Contenu centré
 */
function AppLayoutContent({ children, sidebarLinks }: AppLayoutProps) {
  const { isExpanded, isHovered } = useSidebar();

  // Ajustement dynamique de la marge gauche
  // Sidebar fermée : w-20 (80px) + marge -> ml-28
  // Sidebar ouverte : w-72 (288px) + marge -> ml-80
  const sidebarWidth = isExpanded || isHovered ? 'md:ml-80' : 'md:ml-28';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans text-slate-900">
      {/* Header fixe - desktop uniquement */}
      <div className="hidden md:block">
        <Header />
      </div>

      {/* Actions mobile (notification + logout) - mobile uniquement */}
      <MobileTopActions />

      {/* Container principal */}
      <div className="flex flex-1 pt-0 md:pt-20"> {/* pt-20 pour compenser header h-20 sur desktop */}
        {/* Sidebar */}
        <div className="hidden md:block">
          <Sidebar links={sidebarLinks} />
        </div>

        {/* Main content Area */}
        <main
          className={cn(
            'flex-1 min-w-0 flex flex-col transition-all duration-300 ease-in-out',
            'px-0 pb-24 md:pb-8', // Padding mobile vs desktop
            'pt-14 md:pt-6', // Espace mobile (sous MobileTopActions) vs desktop
            sidebarWidth
          )}
        >
          <div className="flex-1 w-full max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8">
             {/* Effet d'apparition douce du contenu */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {children}
            </div>
          </div>

          <footer className="hidden md:block mt-auto text-center py-6 text-xs text-slate-400">
            © {new Date().getFullYear()} Astéroïde Production Monitoring
          </footer>
        </main>
      </div>
    </div>
  );
}

export function AppLayout(props: AppLayoutProps) {
  const { profile } = useAuth();
  
  return (
    <SidebarProvider>
      <AppLayoutContent {...props} />
      {profile?.role && <MobileBottomBar role={profile.role} permissions={profile.permissions} />}
      {profile?.role === Role.PRESTATAIRE && <GamificationAnimations />}
    </SidebarProvider>
  );
}
